import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { formatChinaDateTime } from '../../utils/date';

type ContactMessage = {
  _id: string;
  name: string;
  email: string;
  organization?: string;
  role?: string;
  subject?: string;
  message: string;
  handled: boolean;
  createdAt: string;
};

const AdminMessages: React.FC = () => {
  const [items, setItems] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get<ContactMessage[]>('/contact-messages');
      setItems(res.data || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Failed to load messages. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleHandled = async (msg: ContactMessage) => {
    if (!msg._id) return;
    try {
      setTogglingId(msg._id);
      await api.put(`/contact-messages/${msg._id}`, {
        handled: !msg.handled,
      });
      setItems((prev) =>
        prev.map((m) =>
          m._id === msg._id ? { ...m, handled: !m.handled } : m
        )
      );
    } catch (err) {
      console.error(err);
      alert('Operation failed. Please try again later.');
    } finally {
      setTogglingId(null);
    }
  };

  const detail = detailId ? items.find((m) => m._id === detailId) : null;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Message Management
        </h1>
      </div>
      <p className="mb-4 text-xs text-neutral-500">
        Messages submitted from the Contact page can be marked as handled.
      </p>

      {loading ? (
        <p className="text-sm text-neutral-600">Loading...</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-subtle">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Name
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Email
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Role/Subject
                </th>
                <th className="max-w-[200px] px-3 py-2 font-semibold text-neutral-700">
                  Message summary
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Submitted At
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Status
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((msg) => (
                <tr
                  key={msg._id}
                  className={`border-b border-neutral-100 ${
                    msg.handled ? 'bg-neutral-50/60' : ''
                  }`}
                >
                  <td className="px-3 py-2 text-neutral-900">{msg.name}</td>
                  <td className="px-3 py-2 text-neutral-700">
                    <a
                      href={`mailto:${msg.email}`}
                      className="text-brand-red hover:underline"
                    >
                      {msg.email}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {[msg.role, msg.subject].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="max-w-[200px] px-3 py-2 text-neutral-600 line-clamp-2">
                    {msg.message}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-neutral-500">
                    {msg.createdAt
                      ? formatChinaDateTime(msg.createdAt)
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        msg.handled
                          ? 'text-neutral-500'
                          : 'font-medium text-amber-600'
                      }
                    >
                      {msg.handled ? 'Handled' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setDetailId(msg._id)}
                      className="mr-2 text-neutral-500 hover:text-brand-red hover:underline"
                    >
                      Details
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleHandled(msg)}
                      disabled={togglingId === msg._id}
                      className="text-brand-red hover:underline disabled:opacity-50"
                    >
                      {togglingId === msg._id
                        ? 'Processing...'
                        : msg.handled
                        ? 'CancelHandled'
                        : 'Mark handled'}
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-neutral-500"
                  >
                    No messages
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/30"
          onClick={() => setDetailId(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-subtle"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-4 text-sm font-semibold text-neutral-900">
              Message details
            </h2>
            <div className="space-y-2 text-xs">
              <p>
                <span className="font-medium text-neutral-600">Name：</span>
                {detail.name}
              </p>
              <p>
                <span className="font-medium text-neutral-600">Email：</span>
                <a
                  href={`mailto:${detail.email}`}
                  className="text-brand-red hover:underline"
                >
                  {detail.email}
                </a>
              </p>
              {(detail.organization || detail.role || detail.subject) && (
                <p>
                  <span className="font-medium text-neutral-600">
                    Organization/Role/Subject:
                  </span>
                  {[detail.organization, detail.role, detail.subject]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </p>
              )}
              <p>
                <span className="font-medium text-neutral-600">Message:</span>
              </p>
              <p className="whitespace-pre-wrap rounded border border-neutral-200 bg-neutral-50 p-3 text-neutral-700">
                {detail.message}
              </p>
              <p className="text-neutral-500">
                Submitted At：
                {detail.createdAt
                  ? formatChinaDateTime(detail.createdAt)
                  : '—'}
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => toggleHandled(detail)}
                disabled={togglingId === detail._id}
                className="rounded-full border border-brand-red px-4 py-1 text-xs font-semibold text-brand-red hover:bg-brand-red hover:text-white disabled:opacity-50"
              >
                {togglingId === detail._id
                  ? 'Processing...'
                  : detail.handled
                  ? 'CancelHandled'
                  : 'Mark handled'}
              </button>
              <button
                type="button"
                onClick={() => setDetailId(null)}
                className="rounded-full border border-neutral-300 px-4 py-1 text-neutral-700 hover:bg-neutral-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
